import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/primitives/Spinner';
import { dragHasFiles } from './imageFiles';
import { SourceEditor } from './SourceEditor';
import s from './MarkdownEditor.module.css';
// The visual editor is a big library: it loads with the first article, not with the panel.
const VisualEditor = lazy(() => import('./VisualEditor'));
/**
 * The article text. Visual by default — it reads the way the site shows it;
 * «Markdown» switches to the source. Either way the text is kept as Markdown.
 */
export function MarkdownEditor({ value, onChange, disabled = false, onUpload }) {
    const [mode, setMode] = useState('visual');
    const [expanded, setExpanded] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [notice, setNotice] = useState(null);
    // Pictures on their way: switching modes now would lose where they go.
    const [pending, setPending] = useState(0);
    const upload = useMemo(() => onUpload &&
        (async (file) => {
            setPending((count) => count + 1);
            try {
                return await onUpload(file);
            }
            finally {
                setPending((count) => count - 1);
            }
        }), [onUpload]);
    useEffect(() => {
        if (!expanded)
            return;
        const closeOnEscape = (event) => {
            if (event.key !== 'Escape')
                return;
            // Esc in the link field closes the field, not the window.
            if (event.target?.closest?.('[data-editor-popup]'))
                return;
            event.preventDefault();
            event.stopPropagation();
            setExpanded(false);
        };
        document.addEventListener('keydown', closeOnEscape, true);
        return () => document.removeEventListener('keydown', closeOnEscape, true);
    }, [expanded]);
    const controls = (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: s.modeButton, "aria-pressed": mode === 'markdown', disabled: pending > 0, title: pending > 0
                    ? 'Дождитесь загрузки картинок'
                    : mode === 'markdown'
                        ? 'Вернуться к обычному виду'
                        : 'Показать исходный Markdown', onClick: () => setMode((current) => (current === 'markdown' ? 'visual' : 'markdown')), children: "Markdown" }), _jsx("button", { type: "button", className: s.toolButton, "aria-label": expanded ? 'Свернуть редактор' : 'Развернуть редактор', title: expanded ? 'Свернуть' : 'Развернуть на всё окно', onClick: () => setExpanded((current) => !current), children: _jsx(Icon, { name: expanded ? 'minimize' : 'maximize', size: 15 }) })] }));
    const modeProps = {
        value,
        onChange,
        disabled,
        expanded,
        upload,
        notify: setNotice,
        controls,
    };
    const editor = (_jsxs("div", { className: [s.shell, expanded && s.expandedShell].filter(Boolean).join(' '), "data-mode": mode, "data-dragging": dragging || undefined, onDragOver: (event) => {
            if (!upload || disabled || !dragHasFiles(event.dataTransfer?.types))
                return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
            setDragging(true);
        }, onDragLeave: (event) => {
            if (!event.currentTarget.contains(event.relatedTarget))
                setDragging(false);
        }, onDrop: () => setDragging(false), children: [mode === 'visual' ? (_jsx(Suspense, { fallback: _jsx("div", { className: s.loading, children: _jsx(Spinner, { size: 16 }) }), children: _jsx(VisualEditor, { ...modeProps }) })) : (_jsx(SourceEditor, { ...modeProps })), notice ? (_jsxs("div", { className: s.notice, role: "alert", children: [_jsx(Icon, { name: "warn", size: 14 }), _jsx("span", { children: notice }), _jsx("button", { type: "button", className: s.noticeClose, "aria-label": "\u0421\u043A\u0440\u044B\u0442\u044C", onClick: () => setNotice(null), children: _jsx(Icon, { name: "x", size: 13 }) })] })) : (_jsxs("div", { className: s.hint, children: [_jsxs("span", { children: [_jsx("kbd", { children: "Shift" }), " + ", _jsx("kbd", { children: "Enter" }), " \u2014 \u043F\u0435\u0440\u0435\u043D\u043E\u0441 \u0441\u0442\u0440\u043E\u043A\u0438 \u0431\u0435\u0437 \u043D\u043E\u0432\u043E\u0433\u043E \u0430\u0431\u0437\u0430\u0446\u0430"] }), onUpload && _jsx("span", { children: "\u043A\u0430\u0440\u0442\u0438\u043D\u043A\u0443 \u043C\u043E\u0436\u043D\u043E \u0432\u0441\u0442\u0430\u0432\u0438\u0442\u044C \u0438\u0437 \u0431\u0443\u0444\u0435\u0440\u0430 \u0438\u043B\u0438 \u043F\u0435\u0440\u0435\u0442\u0430\u0449\u0438\u0442\u044C" })] }))] }));
    if (!expanded)
        return editor;
    return createPortal(_jsxs("div", { className: s.fullscreen, role: "dialog", "aria-modal": "true", "aria-label": "\u041F\u043E\u043B\u043D\u043E\u044D\u043A\u0440\u0430\u043D\u043D\u044B\u0439 \u0440\u0435\u0434\u0430\u043A\u0442\u043E\u0440 \u0441\u0442\u0430\u0442\u044C\u0438", children: [_jsxs("header", { className: s.fullscreenHeader, children: [_jsxs("div", { children: [_jsx("h2", { children: "\u0422\u0435\u043A\u0441\u0442 \u0441\u0442\u0430\u0442\u044C\u0438" }), _jsx("p", { children: "\u0421\u043E\u0445\u0440\u0430\u043D\u044F\u0435\u0442\u0441\u044F \u0432\u043C\u0435\u0441\u0442\u0435 \u0441\u043E \u0441\u0442\u0430\u0442\u044C\u0451\u0439" })] }), _jsx("button", { type: "button", className: s.doneButton, onClick: () => setExpanded(false), children: "\u0413\u043E\u0442\u043E\u0432\u043E" })] }), editor] }), document.body);
}
